/**
 * Profile API service — mirrors Flutter WebHomeRepository + IApplicationRepository
 * profile-related endpoints.
 *
 * Reuses existing authApi.getUserDetails / updateUserDetails where possible.
 * Adds profile-specific endpoints: applied/invited/recommended/saved jobs,
 * delete account, location suggestions for profile form.
 *
 * Flutter source:
 *   lib/infrastructure/web_home/i_web_home_repository.dart
 *   lib/infrastructure/application/i_application_repository.dart
 *
 * The My Jobs list calls come from IApplicationRepository, which uses plain http (no
 * HttpInterCeptor), so they pass skipErrorInterceptor. The candidate and account calls
 * come from IWebHomeRepository's dio and keep the interceptor.
 */
import { FirebaseError } from 'firebase/app';
import apiClient from './apiClient';
import { auth } from '@/config/firebase';
import { useAppStateStore } from '@/store/appStateStore';
import { AUTH_API_URL } from '@/config/env';
import type {
  CandidateDetails,
  ViewJobsData,
  RecommendedJobsResponse,
} from '@/types/profile.types';
import type { Aggregation } from '@/types/job.types';

// Re-export candidate CRUD from authApi for convenience
export { getUserDetails, updateUserDetails, getEditProfileFormContent } from './authApi';

/* ────────────────────────────── Applied Jobs ────────────────────────────── */

/**
 * GET /v1/application/_applied?offset={offset}&limit={limit}
 * Mirrors Flutter IApplicationRepository.getAppliedApplications().
 */
export async function getAppliedJobs(
  offset = 0,
  limit = 6,
): Promise<ViewJobsData[]> {
  const res = await apiClient.get<ViewJobsData[]>(
    `${AUTH_API_URL}/application/_applied?offset=${offset}&limit=${limit}`,
    { skipErrorInterceptor: true },
  );
  return res.data ?? [];
}

/* ────────────────────────────── Invited Jobs ────────────────────────────── */

/**
 * GET /v1/candidate/_invitedJobs?offset={offset}&limit={limit}
 * Mirrors Flutter IApplicationRepository.getInvitedJobs().
 */
export async function getInvitedJobs(
  offset = 0,
  limit = 6,
): Promise<ViewJobsData[]> {
  const res = await apiClient.get<ViewJobsData[]>(
    `${AUTH_API_URL}/candidate/_invitedJobs?offset=${offset}&limit=${limit}`,
    { skipErrorInterceptor: true },
  );
  return res.data ?? [];
}

/* ────────────────────────────── Recommended Jobs ────────────────────────────── */

/**
 * GET /v1/requisition/recommendations?page={page}&size={size}
 * Mirrors Flutter IApplicationRepository.getRecommendedJobs().
 */
export async function getRecommendedJobs(
  page = 1,
  size = 6,
  filters?: Record<string, string[]>,
): Promise<RecommendedJobsResponse> {
  let query = '';
  if (filters) {
    const parts: string[] = [];
    for (const [key, values] of Object.entries(filters)) {
      if (values.length > 0) {
        parts.push(`${key}=${values.map((v) => encodeURIComponent(v)).join('$:')}`);
      }
    }
    if (parts.length) {
      query = `&${parts.join('&')}`;
    }
  }
  const res = await apiClient.get<RecommendedJobsResponse>(
    `${AUTH_API_URL}/requisition/recommendations?page=${page}&size=${size}${query}`,
    { skipErrorInterceptor: true },
  );
  return res.data;
}

/**
 * GET /v1/requisition/aggregation/recommendations
 * Mirrors Flutter IApplicationRepository.getRecommendedJobsAggregation().
 */
export async function getRecommendedJobsAggregation(
  filters?: Record<string, string[]>,
): Promise<{ aggregations?: Aggregation[] }> {
  let query = '';
  if (filters) {
    const parts: string[] = [];
    for (const [key, values] of Object.entries(filters)) {
      if (values.length > 0) {
        parts.push(`${key}=${values.map((v) => encodeURIComponent(v)).join('$:')}`);
      }
    }
    if (parts.length) {
      query = `?${parts.join('&')}`;
    }
  }
  const res = await apiClient.get<{ aggregations?: Aggregation[] }>(
    `${AUTH_API_URL}/requisition/aggregation/recommendations${query}`,
    { skipErrorInterceptor: true },
  );
  return res.data;
}

/* ────────────────────────────── Saved Jobs ────────────────────────────── */

/**
 * GET /v1/candidate/_savedJobs?offset={offset}&limit={limit}
 * Mirrors Flutter IApplicationRepository.getSavedJobs().
 */
export async function getSavedJobs(
  offset = 0,
  limit = 6,
): Promise<ViewJobsData[]> {
  const res = await apiClient.get<ViewJobsData[]>(
    `${AUTH_API_URL}/candidate/_savedJobs?offset=${offset}&limit=${limit}`,
    { skipErrorInterceptor: true },
  );
  return res.data ?? [];
}

/* ────────────────────────────── Delete Account ────────────────────────────── */

/**
 * DELETE /v1/account
 * Mirrors Flutter IWebHomeRepository.deleteUserAccount().
 */
export async function deleteUserAccount(): Promise<boolean> {
  const res = await apiClient.delete(`${AUTH_API_URL}/account`);
  return res.status === 200;
}

/* ────────────────────────────── Candidate Details (profile-specific) ────────────────────────────── */

/** Flutter ErrorConstants.sessionExpired */
export const SESSION_EXPIRED_ERROR = 'Your session has expired. Please login again.';

/**
 * GET /v1/candidate
 * Alternative endpoint used by profile to get full CandidateDetails.
 * Mirrors Flutter IWebHomeRepository.getUserDetails() which uses /v1/candidate: it first calls
 * FirebaseAuth.currentUser.reload(), and a FirebaseAuthException there fails with
 * ErrorConstants.sessionExpired (thrown here as `new Error(SESSION_EXPIRED_ERROR)`).
 */
export async function getCandidateDetails(): Promise<CandidateDetails> {
  try {
    await auth.currentUser?.reload();
  } catch (error) {
    if (error instanceof FirebaseError && error.code.startsWith('auth/')) {
      throw new Error(SESSION_EXPIRED_ERROR);
    }
    throw error;
  }
  const res = await apiClient.get<CandidateDetails>(
    `${AUTH_API_URL}/candidate`,
  );
  // Header name — Flutter WebProfileBloc → AppStateNotifier.setCandidateDetails
  useAppStateStore.getState().setCandidateDetails(res.data as unknown as Record<string, unknown>);
  return res.data;
}

/**
 * Mirrors Flutter CandidateDetails.fromJson(): keeps only the model's fields, and a missing
 * value becomes '' (text) or 0 (timestamps, experience). Any other key is dropped.
 */
export function candidateDetailsFromJson(json: Record<string, unknown>): CandidateDetails {
  const text = (key: string) => (json[key] ?? '') as string;
  const number = (key: string) => (json[key] ?? 0) as number;
  return {
    id: text('id'),
    displayId: text('displayId'),
    firstName: text('firstName'),
    lastName: text('lastName'),
    primaryEmailId: text('primaryEmailId'),
    secondaryEmailId: text('secondaryEmailId'),
    primaryContactNumber: text('primaryContactNumber'),
    secondaryContactNumber: text('secondaryContactNumber'),
    createdBy: text('createdBy'),
    updatedBy: text('updatedBy'),
    createdOn: number('createdOn'),
    updatedOn: number('updatedOn'),
    workspaceId: text('workspaceId'),
    currentRole: text('currentRole'),
    currentLocation: text('currentLocation'),
    linkedInUrl: text('linkedInUrl'),
    candidateUrl: text('candidateUrl'),
    experienceInMonths: number('experienceInMonths'),
    resumeUpdateOn: number('resumeUpdateOn'),
    secondaryContactNumberCountryCode: text('secondaryContactNumberCountryCode'),
    primaryContactNumberCountryCode: text('primaryContactNumberCountryCode'),
    isMfaEnabled: (json.isMfaEnabled ?? undefined) as boolean | undefined,
  };
}

/**
 * Mirrors Flutter CandidateDetails.toJson(): the 17 fields sent to PUT /v1/candidate, plus
 * isMfaEnabled when it is set. createdBy / updatedBy / createdOn / updatedOn are never sent.
 */
export function candidateDetailsToJson(details: CandidateDetails): Record<string, unknown> {
  const json: Record<string, unknown> = {
    id: details.id,
    currentRole: details.currentRole,
    currentLocation: details.currentLocation,
    primaryEmailId: details.primaryEmailId,
    secondaryEmailId: details.secondaryEmailId,
    primaryContactNumber: details.primaryContactNumber,
    secondaryContactNumber: details.secondaryContactNumber,
    displayId: details.displayId,
    firstName: details.firstName,
    lastName: details.lastName,
    workspaceId: details.workspaceId,
    linkedInUrl: details.linkedInUrl,
    candidateUrl: details.candidateUrl,
    experienceInMonths: details.experienceInMonths,
    secondaryContactNumberCountryCode: details.secondaryContactNumberCountryCode,
    primaryContactNumberCountryCode: details.primaryContactNumberCountryCode,
    resumeUpdateOn: details.resumeUpdateOn,
  };
  if (details.isMfaEnabled != null) json.isMfaEnabled = details.isMfaEnabled;
  return json;
}

/**
 * PUT /v1/candidate
 * Mirrors Flutter IWebHomeRepository.updateUserDetails(): dio.put(url, data: [candidateDetails.toJson()]).
 */
export async function updateCandidateDetails(
  details: CandidateDetails,
): Promise<unknown> {
  const res = await apiClient.put(`${AUTH_API_URL}/candidate`, [candidateDetailsToJson(details)]);
  return res.data;
}
